import React from 'react';

const DEV_RELOAD_KEY_PREFIX = 'ax1.lazy-reload';
let implicitModuleKey = 0;

// React.ComponentType is invariant because of legacy static fields such as
// propTypes. `any` is required at this one boundary to accept components with
// arbitrary props while the generic T preserves each imported component type.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type LoadableComponent = React.ComponentType<any>;

function isTransientDynamicImportError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return /Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError|Loading chunk\s+\d+\s+failed/i.test(message);
}

/**
 * Protects React.lazy imports from transient stale-module and chunk errors.
 * It performs at most one hard reload per module key, then surfaces the
 * original error if the import still fails. The implicit key is stable across
 * full reloads because lazy declarations execute in deterministic order.
 */
export function lazyWithRecovery<T extends LoadableComponent>(
  importer: () => Promise<{ default: T }>,
  moduleKey = `module-${implicitModuleKey++}`,
): React.LazyExoticComponent<T> {
  return React.lazy(async () => {
    try {
      const mod = await importer();
      if (typeof window !== 'undefined' && typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem(`${DEV_RELOAD_KEY_PREFIX}:${moduleKey}`);
      }
      return mod;
    } catch (error) {
      const canAutoReload =
        typeof window !== 'undefined' &&
        typeof sessionStorage !== 'undefined' &&
        isTransientDynamicImportError(error);

      if (canAutoReload) {
        const storageKey = `${DEV_RELOAD_KEY_PREFIX}:${moduleKey}`;
        const alreadyRetried = sessionStorage.getItem(storageKey) === '1';

        if (!alreadyRetried) {
          sessionStorage.setItem(storageKey, '1');
          window.location.reload();
          // Keep suspense fallback visible while the reload starts.
          return new Promise<never>(() => {});
        }
      }

      throw error;
    }
  });
}

/** @deprecated Use lazyWithRecovery for new route declarations. */
export function lazyWithDevReload<T extends LoadableComponent>(
  importer: () => Promise<{ default: T }>,
  moduleKey: string,
): React.LazyExoticComponent<T> {
  return lazyWithRecovery(importer, moduleKey);
}
